import { clearWorkosSessionCookies, buildWorkosLogoutUrl } from './workos'
import { getOrigin } from './origin'
import useAuth from '../models/useAuth'
import { logAuthEvent } from './workos-token-utils'
import controller from '../controller'
import { markSignedOut } from '../utilities/silentSignIn'
import { inNativeApp } from './nativeApp'

/**
 * Shared sign-out function that handles controller cleanup, WorkOS cookie clearing,
 * and redirect to WorkOS logout URL for SSO logout.
 */
export const performSignOut = async () => {
  logAuthEvent('performSignOut called')
  // The sign-in page this lands on must not sign the visitor straight back in.
  markSignedOut()

  const { workosSessionId, setWorkosSessionId } = useAuth.getState()
  const sessionId = workosSessionId

  try {
    // Wait for controller cleanup with timeout protection
    const cleanupPromise = controller.signOut()
    const timeoutPromise = new Promise<void>((resolve) =>
      setTimeout(() => {
        logAuthEvent('Controller cleanup timeout - proceeding with sign out')
        resolve()
      }, 3000)
    )

    await Promise.race([cleanupPromise, timeoutPromise])
    logAuthEvent('Controller cleanup completed')

    // Clear WorkOS state and cookies
    setWorkosSessionId(null)
    await clearWorkosSessionCookies(sessionId)
    logAuthEvent('WorkOS cookies cleared')

    const origin = getOrigin()
    const returnTo = origin ? `${origin}/login` : undefined

    // Build WorkOS logout URL and redirect
    if (sessionId && inNativeApp) {
      // The noBGP app opens a host outside its allow-list in Safari or Chrome, which took the
      // person out of the app. The logout link ends the WorkOS session on WorkOS's side, so the
      // app sends it without following it, then stays on its own sign-in page.
      logAuthEvent('Ending the WorkOS session in the background', { returnTo })
      await fetch(buildWorkosLogoutUrl(sessionId), { mode: 'no-cors', credentials: 'omit', redirect: 'manual' }).catch(
        () => undefined,
      )
      window.location.assign(returnTo || '/login')
    } else if (sessionId) {
      logAuthEvent('Redirecting to WorkOS logout', { returnTo })
      window.location.assign(buildWorkosLogoutUrl(sessionId, returnTo))
    } else {
      // Fallback if no session ID
      logAuthEvent('No WorkOS session ID - redirecting to login', { returnTo })
      window.location.assign(returnTo || '/login')
    }
  } catch (error) {
    logAuthEvent('Error during sign out', { error })
    // Even if cleanup fails, still redirect to login
    const origin = getOrigin()
    const returnTo = origin ? `${origin}/login` : '/login'
    window.location.assign(returnTo)
  }
}
